import {
  compareRepresentativeRelationships,
  endpointRelativeRelationshipLabel,
} from '@entangled-vega/shared';

import type { GraphRelationship } from '../data/graph-types.js';

export const RELATIONSHIP_FILTERS = [
  'spouses',
  'partners',
  'children',
  'parents',
  'siblings',
  'extended',
] as const;

export type RelationshipFilter = (typeof RELATIONSHIP_FILTERS)[number];

export const FOCUS_DEPTHS = ['direct', 'two-hop', 'universe'] as const;
export type FocusDepth = (typeof FOCUS_DEPTHS)[number];

export const RELATIONSHIP_FILTER_LABELS: Readonly<Record<RelationshipFilter, string>> = {
  spouses: 'Spouses',
  partners: 'Partners',
  children: 'Children',
  parents: 'Parents',
  siblings: 'Siblings',
  extended: 'Extended',
};

export const FOCUS_DEPTH_LABELS: Readonly<Record<FocusDepth, string>> = {
  direct: 'Direct',
  'two-hop': '2 hops',
  universe: 'Universe',
};

function counterpartRole(relationship: GraphRelationship, personId: string): string | undefined {
  const direction = relationship.direction;
  if (direction === undefined) {
    return undefined;
  }
  if (direction.subject === personId) {
    return direction.objectRole;
  }
  if (direction.object === personId) {
    return direction.subjectRole;
  }
  return undefined;
}

export function relationshipFilterForPerson(
  relationship: GraphRelationship,
  personId: string,
): RelationshipFilter {
  switch (relationship.relation) {
    case 'spouse':
      return 'spouses';
    case 'unmarried-partner':
      return 'partners';
    case 'sibling':
      return 'siblings';
    case 'parent-child':
    case 'stepparent': {
      const role = counterpartRole(relationship, personId);
      if (role === 'child' || role === 'stepchild') {
        return 'children';
      }
      if (role === 'parent' || role === 'stepparent') {
        return 'parents';
      }
      return 'extended';
    }
    case 'relative':
    case 'godparent':
      return 'extended';
    case 'professional-collaborator':
    case 'student-of':
    case 'doctoral-advisor':
    case 'appointed-by':
    case 'creative-collaborator':
    case 'organizational-collaborator':
      return 'extended';
  }
}

export function relationshipLabelForPerson(
  relationship: GraphRelationship,
  personId: string,
): string {
  return endpointRelativeRelationshipLabel(relationship, personId);
}

function normalized(value: string): string {
  return value.toLocaleLowerCase('en-US').replaceAll(/\s+/gu, ' ').trim();
}

export function usefulRelationshipNote(
  relationship: GraphRelationship,
  personId: string,
): string | null {
  if (relationship.note === undefined) {
    return null;
  }
  const note = normalized(relationship.note);
  const label = normalized(relationshipLabelForPerson(relationship, personId));
  const genericDirection =
    relationship.direction === undefined
      ? null
      : normalized(`${relationship.direction.subjectRole} / ${relationship.direction.objectRole}`);
  if (
    note === label ||
    note === normalized(relationship.label) ||
    note === genericDirection ||
    ['family', 'family relation', 'relative'].includes(note)
  ) {
    return null;
  }
  return relationship.note;
}

export function relationshipMatchesFilters(
  relationship: GraphRelationship,
  personId: string,
  enabled: ReadonlySet<RelationshipFilter> | readonly RelationshipFilter[],
): boolean {
  const enabledSet = enabled instanceof Set ? enabled : new Set(enabled);
  return enabledSet.has(relationshipFilterForPerson(relationship, personId));
}

export function filteredRepresentativeRelationship(
  phases: readonly GraphRelationship[],
  personId: string,
  enabled: ReadonlySet<RelationshipFilter> | readonly RelationshipFilter[],
): GraphRelationship | null {
  return (
    phases
      .filter((phase) => relationshipMatchesFilters(phase, personId, enabled))
      .sort(compareRepresentativeRelationships)[0] ?? null
  );
}
